import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import useUnitProvider from '/@/core/context/unit';
import { useReports } from '/@/hooks/reports';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { formatDate } from '/@/utils';
import { matchesCrop } from '../fieldview-crop';

/** Max span (in months) after the planting date to look for a harvest report. */
export const HARVEST_WINDOW_MONTHS = 12;

export type FormValues = {
    harvestDate: Dayjs | null;
    harvestResultId: number | null;
    harvestRequestId: number | null;
    yield: number | null;
    humidity: number | null;
};

export type DateOption = {
    value: number;
    label: string;
    date: Dayjs;
    requestId: number;
};

/** Minimal shapes of the FieldView report payloads consumed here (the SWR
 * `useReports` response is otherwise untyped). */
type FvHarvestResult = {
    id: number;
    report: number;
    cropName: string;
    harvestDates: string[];
    totalHarvestedArea: number;
    climateCalculatedAverageYield: number;
    totalWetMass: number;
    totalMoistureMass: number;
};
type FvReportRow = { status?: string; results?: FvHarvestResult[] };

const rowsOf = (query: ReturnType<typeof useReports>): FvReportRow[] => (query.data?.results as FvReportRow[]) || [];
const someRowPending = (query: ReturnType<typeof useReports>) => rowsOf(query).some((r) => r.status === 'pending');

const usePendingReportsRetry = (query: ReturnType<typeof useReports>, intervalMs = 5000) => {
    const isPending = someRowPending(query);
    const { mutate } = query;
    useEffect(() => {
        if (!isPending) return;
        const interval = setInterval(() => {
            mutate();
        }, intervalMs);
        return () => clearInterval(interval);
    }, [isPending, mutate, intervalMs]);
};

export const useReportForm = ({
    field,
    cropMonitoring,
    isFieldviewType,
    enabled,
    reportsMutating,
    reportsError,
}: {
    field: Field;
    cropMonitoring: CropMonitoring;
    isFieldviewType: boolean;
    /** Only fetch FieldView reports while the modal is open. */
    enabled: boolean;
    reportsMutating: boolean;
    reportsError: unknown;
}) => {
    const units = useUnitProvider();

    /** The campaign planting date anchors the window to look for harvests in. */
    const plantingAnchor = useMemo(
        () => (cropMonitoring?.plantingDate ? dayjs(cropMonitoring.plantingDate) : null),
        [cropMonitoring?.plantingDate]
    );

    const form = useForm<FormValues>({
        defaultValues: {
            humidity: 14.5,
        },
    });

    // --- Harvest options: FieldView harvest results within [planting, planting + 1y] ---
    const harvestReportsQuery = useReports({ fieldId: field.id, type: 'harvest' }, { enabled });
    usePendingReportsRetry(harvestReportsQuery);

    const validHarvestResults = useMemo<FvHarvestResult[]>(() => {
        if (!plantingAnchor) return [];
        const windowEnd = plantingAnchor.add(HARVEST_WINDOW_MONTHS, 'month');
        return rowsOf(harvestReportsQuery).flatMap((report) =>
            (report.results || []).filter((result) => {
                if (!matchesCrop(result.cropName, cropMonitoring.crop)) return false;
                const harvestDate = dayjs(result.harvestDates[0]);
                return !harvestDate.isBefore(plantingAnchor, 'day') && !harvestDate.isAfter(windowEnd, 'day');
            })
        );
    }, [harvestReportsQuery, plantingAnchor, cropMonitoring.crop]);

    const harvestDateOptions = useMemo<DateOption[]>(() => {
        const today = dayjs();
        return validHarvestResults
            .map(
                (result): DateOption => ({
                    value: result.id,
                    label: `${formatDate(dayjs(result.harvestDates[0]).toDate())} (${_('Area cosechada:')} ${units.toArea(result.totalHarvestedArea, 2).toString()})`,
                    date: dayjs(result.harvestDates[0]),
                    requestId: result.report,
                })
            )
            .toSorted((a, b) => Math.abs(a.date.diff(today, 'day')) - Math.abs(b.date.diff(today, 'day')));
    }, [validHarvestResults, units]);

    // --- Auto-select nearest harvest option ---
    useEffect(() => {
        const nearest = harvestDateOptions[0];
        if (nearest) {
            form.setValue('harvestDate', nearest.date);
            form.setValue('harvestResultId', nearest.value);
            form.setValue('harvestRequestId', nearest.requestId);
        }
    }, [harvestDateOptions, form]);

    // --- Yield & humidity prefilled from the selected harvest result ---
    const harvestResultId = form.watch('harvestResultId');
    useEffect(() => {
        const result = validHarvestResults.find((r) => r.id === harvestResultId);
        if (result) {
            form.setValue('yield', units.toYield(result.climateCalculatedAverageYield, 'corn', 2).value);
            // Humidity is a percentage of the wet mass, so moisture over wet mass — not
            // the other way round.
            form.setValue(
                'humidity',
                result.totalWetMass ? round((result.totalMoistureMass / result.totalWetMass) * 100, 2) : null
            );
        }
    }, [harvestResultId, validHarvestResults, form, units]);

    const reportsPending = useMemo(() => {
        if (!isFieldviewType) return false;
        return someRowPending(harvestReportsQuery) || reportsMutating || (!harvestReportsQuery.data && !reportsError);
    }, [isFieldviewType, harvestReportsQuery, reportsMutating, reportsError]);

    const hasHarvestData = harvestDateOptions.length > 0;
    const harvestDataResolved = isFieldviewType
        ? !reportsPending && (Boolean(harvestReportsQuery.data) || Boolean(reportsError))
        : true;

    /** Harvest resolved from FieldView → we're confirming, not entering, data. */
    const harvestAutoSelected = Boolean(harvestResultId);

    return {
        form,
        units,
        harvestDateOptions,
        harvestAutoSelected,
        reportsPending,
        hasHarvestData,
        harvestDataResolved,
    };
};
